/* SUMMARY
  * Imports
  * CustomerNoteSticky: a customer's note as a post-it, pinned to the top of a page
*/

/* Imports */
import { Typography } from "@material-tailwind/react";
import { t } from "i18next";
/***/

/* Components */
import { isBlankNote } from "./CustomerNoteCallout";
/***/

/**
 * CustomerNoteSticky: the quiet replacement for the pop-up on the intervention detail.
 *
 * The pop-up was doing its job too well -- it fired on every intervention a planner opened, and a
 * modal that has to be dismissed before any work can start gets clicked away unread. A post-it is
 * the trade: impossible to miss at the top of the page, impossible to be blocked by.
 *
 * It reads as a physical note rather than as another card in the layout: the tilt and the shadow
 * are what make it a post-it and not a warning banner, and they are the reason it is seen without
 * having to shout. Kept to a slight angle so the text stays comfortable to read.
 *
 * Renders nothing when the note is blank, so a caller cannot pin an empty square to the page.
 *
 * @param note - the note text
 * @param customerName - whose note this is, since the sticky sits away from the customer field
 * @param className - additional classnames
 */
export default function CustomerNoteSticky({
  note,
  customerName,
  className = ""
}: {
  note?: string | null,
  customerName?: string,
  className?: string
}) {
  if (isBlankNote(note)) return null;

  return (
    <div className={`mb-4 flex ${className}`}>
      <div
        // -rotate-1 with hover:rotate-0: the tilt sells the post-it, and straightening it under the
        // cursor makes a long note easier to read without turning it into just another card.
        className="w-full max-w-md -rotate-1 rounded-sm bg-yellow-200 p-4 shadow-lg
                   transition-transform hover:rotate-0 sm:w-auto sm:min-w-[20rem]"
        data-testid="customer-note-sticky"
      >
        <Typography variant="small" className="mb-2 font-bold text-yellow-900">
          {/* The pin is the post-it's own mark; no icon component, so it cannot drift in size. */}
          {"\u{1F4CC} "}
          {t("client:form:fields:plannerNote:label")}
          {customerName && ` — ${customerName}`}
        </Typography>

        {/*
          * max-h + overflow-y so a note at the 1000-character ceiling scrolls inside the post-it
          * instead of pushing the tabs off the fold.
          * whitespace-pre-line: planners write these as checklists, one instruction per line.
          */}
        <Typography className="max-h-56 overflow-y-auto whitespace-pre-line break-words text-yellow-900">
          {note}
        </Typography>
      </div>
    </div>
  );
}
/***/
